import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useInView } from 'react-intersection-observer';

import { Text, Preset, FunctionColor } from '@lux/text';
import { Media, SnakeToCamelObject } from '@browsing/api';
import { trackSelectPromotion } from '@browsing/tracking';

import { Campaign } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { buildCampaignUrl } from '@client/helpers/campaignUrl';
import { NEW_DEALS_MOBILE_FIXED_POSITION } from '@client/config/constants';

import * as S from './carousel-card-new-deals-today.styles';
import Discount from '../campaigns/components/discount';

interface CarouselCardNewDealsTodayProps {
    campaign: Campaign;
    media: Omit<SnakeToCamelObject<Media>, 'path'>;
    onClick?: () => void;
    index: number;
    onInView?: (campaign: Campaign, isVisible: boolean) => void;
}

const CarouselCardNewDealsToday = ({
    campaign,
    media,
    onClick = () => {},
    index,
    onInView,
}: CarouselCardNewDealsTodayProps) => {
    const { campaignId } = campaign;
    const categoryTabId = useSelector(getCategoryTabId);
    const destination = buildCampaignUrl(campaign.campaignId, categoryTabId);

    const { ref, inView } = useInView({
        threshold: 0.1,
        triggerOnce: false,
    });

    useEffect(() => {
        if (onInView) {
            onInView(campaign, inView);
        }
    }, [inView, campaign, onInView]);

    const handleClick = () => {
        trackSelectPromotion({
            element: 'deals_today_carousel',
            creativeName: 'deals_today_carousel',
            itemListName: 'deals_today_carousel',
            creativeSlot: NEW_DEALS_MOBILE_FIXED_POSITION,
            item: {
                campaignIdentifier: campaign.campaignId,
                categoryId: categoryTabId,
                index,
            },
        });

        onClick();
    };

    return (
        <S.CarouselCardNewDealsTodayWrapper
            ref={ref}
            id={campaignId}
            href={destination}
            onClick={handleClick}
        >
            <S.ImageWrapper>
                <S.NewDealsImage src={media.url} alt={media.altText} />
            </S.ImageWrapper>
            <S.ContentWrapper>
                <S.CampaignTitle>
                    <Text preset={Preset.S} color={FunctionColor.function100} bold>
                        {campaign.name}
                    </Text>
                </S.CampaignTitle>

                <S.SpacerXS />
                <Discount campaign={campaign} />
            </S.ContentWrapper>
        </S.CarouselCardNewDealsTodayWrapper>
    );
};

export { CarouselCardNewDealsToday };
